'use client';

import { c as _c } from "react/compiler-runtime";
import { StatusEnum } from '@/types';
import classNames from '@/utils/classNames';
import { CloseIcon, CheckIcon } from '@/icons/solid/12';
import Tooltip from '@/components/Particles/Tooltip';
import { bytesToMB } from '@/utils/stringUtils';
import IconContainer from '../IconContainer';
import useUploadPendingAttachment from '../../hooks/useUploadPendingAttachment';
import AttachmentLabel from './AttachmentsToolbarItemLabel';
import type { AttachmentUploadState } from '../../AttachmentsToolbar.types';
const baseClassName = ['h-14', 'flex', 'items-center', 'justify-between', 'gap-4', 'border-t', 'border-t-stone-300', 'px-4'].join(' ');
type AttachmentProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> & AttachmentUploadState & {
  remove: () => void;
};
const AttachmentsToolbarItem = t0 => {
  const $ = _c(45);
  let attachment_id;
  let className;
  let file;
  let name;
  let props;
  let remove;
  let status;
  let t1;
  if ($[0] !== t0) {
    const {
      attachment_id: t2,
      name: t3,
      file: t4,
      progress: t5,
      status: t6,
      remove: t7,
      className: t8,
      pre_signed_url,
      url,
      entity_id,
      entity_type,
      ...t9
    } = t0;
    attachment_id = t2;
    name = t3;
    file = t4;
    t1 = t5;
    status = t6;
    remove = t7;
    className = t8;
    props = t9;
    $[0] = t0;
    $[1] = attachment_id;
    $[2] = className;
    $[3] = file;
    $[4] = name;
    $[5] = props;
    $[6] = remove;
    $[7] = status;
    $[8] = t1;
  } else {
    attachment_id = $[1];
    className = $[2];
    file = $[3];
    name = $[4];
    props = $[5];
    remove = $[6];
    status = $[7];
    t1 = $[8];
  }
  const progress = t1 === undefined ? 0 : t1;
  const abortUpload = useUploadPendingAttachment(attachment_id);
  const Icon = status === StatusEnum.DONE ? CheckIcon : CloseIcon;
  let t2;
  if ($[9] !== className) {
    t2 = classNames(baseClassName, className);
    $[9] = className;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== progress) {
    t3 = {
      "--file-upload-progress": progress
    };
    $[11] = progress;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = status === StatusEnum.PENDING;
  let t5;
  if ($[13] !== name) {
    t5 = <AttachmentLabel.Name>{name}</AttachmentLabel.Name>;
    $[13] = name;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== file.size) {
    t6 = bytesToMB(file.size);
    $[15] = file.size;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <AttachmentLabel.Size>{t6}</AttachmentLabel.Size>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t4 || $[20] !== t5 || $[21] !== t7) {
    t8 = <AttachmentLabel isUploading={t4}>{t5}{t7}</AttachmentLabel>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== abortUpload || $[24] !== remove || $[25] !== status) {
    t9 = status === StatusEnum.DONE ? {
      as: "span",
      className: "text-green-500"
    } : {
      onClick: () => {
        abortUpload && abortUpload?.();
        remove();
      },
      className: classNames({
        "text-red-500": status === StatusEnum.FAILED
      })
    };
    $[23] = abortUpload;
    $[24] = remove;
    $[25] = status;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const t10 = status === StatusEnum.FAILED ? `Failed to upload file: ${name}` : status === StatusEnum.DONE ? "File uploaded" : status === null ? "Waiting to upload file..." : "Uploading file...";
  const t11 = status === StatusEnum.FAILED;
  const t12 = status === StatusEnum.DONE;
  let t13;
  if ($[27] !== t11 || $[28] !== t12) {
    t13 = classNames("whitespace-break-spaces p-2 text-xs", {
      "bg-red-500 text-white": t11,
      "bg-green-500 text-white": t12
    });
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== Icon) {
    t14 = <Icon />;
    $[30] = Icon;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t10 || $[33] !== t13 || $[34] !== t14) {
    t15 = <Tooltip content={t10} className={t13}>{t14}</Tooltip>;
    $[32] = t10;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15 || $[37] !== t9) {
    t16 = <IconContainer {...t9}>{t15}</IconContainer>;
    $[36] = t15;
    $[37] = t9;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== props || $[40] !== t16 || $[41] !== t2 || $[42] !== t3 || $[43] !== t8) {
    t17 = <div className={t2} style={t3} {...props}>{t8}{t16}</div>;
    $[39] = props;
    $[40] = t16;
    $[41] = t2;
    $[42] = t3;
    $[43] = t8;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
};
export default AttachmentsToolbarItem;